<script lang="ts">
  /**
   * SkillPicker — popover used by both the slash-menu trigger and the
   * Sparkles button. Filters BUILTIN_SKILLS by query, supports keyboard
   * navigation, and calls onSelect with the chosen SkillMeta.
   */
  import { Sparkles, SearchCheck } from "lucide-svelte";
  import type { SvelteComponent } from "svelte";
  import type { SkillMeta } from "../../chat/types";
  import { filterSkills } from "../../chat/builtin-skills";
  import { i18n } from "../../chat/i18n.svelte";

  const { t } = i18n;

  interface Props {
    open: boolean;
    /** Source list (typically runtime state's skills, fetched from GET /skills). */
    skills: SkillMeta[];
    /** Search query — typically the slash query (text after the trigger /) */
    query: string;
    /** Anchor offset above the input container (px). Default 8. */
    anchorOffset?: number;
    onSelect: (skill: SkillMeta) => void;
    onClose: () => void;
  }

  let { open, skills, query, anchorOffset = 8, onSelect, onClose }: Props = $props();

  const results = $derived(filterSkills(skills, query));
  let highlighted = $state(0);

  // Reset highlight when results shrink/grow so we don't point past the end.
  $effect(() => {
    if (highlighted >= results.length) highlighted = 0;
  });

  // Reset highlight when popover opens
  $effect(() => {
    if (open) highlighted = 0;
  });

  const iconMap: Record<string, typeof SvelteComponent<any>> = {
    Sparkles,
    SearchCheck,
  };

  function iconFor(name: string | undefined) {
    if (!name) return Sparkles;
    return iconMap[name] ?? Sparkles;
  }

  export function handleKeydown(e: KeyboardEvent): boolean {
    if (!open) return false;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (results.length > 0) highlighted = (highlighted + 1) % results.length;
      return true;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (results.length > 0) highlighted = (highlighted - 1 + results.length) % results.length;
      return true;
    }
    if (e.key === "Enter") {
      if (results[highlighted]) {
        e.preventDefault();
        onSelect(results[highlighted]);
        return true;
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return true;
    }
    return false;
  }
</script>

{#if open}
  <div
    class="fluent-fade-in"
    role="listbox"
    aria-label={t("skillsPickerTitle")}
    style="
      position: absolute;
      bottom: 100%;
      left: 0;
      right: 0;
      margin-bottom: {anchorOffset}px;
      background: var(--chat-bg-tertiary);
      border: 1px solid var(--chat-border-active);
      border-radius: var(--chat-radius-lg);
      box-shadow: var(--chat-shadow-lg);
      z-index: 200;
      overflow: hidden;
      padding: 6px;
      max-height: 280px;
      display: flex;
      flex-direction: column;
    "
  >
    <div style="
      padding: 4px 8px 6px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--chat-border);
      margin-bottom: 4px;
    ">
      <span style="
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--chat-text-muted);
      ">{t("skillsPickerTitle")}</span>
      <span style="
        font-size: 10px;
        color: var(--chat-text-muted);
        font-style: italic;
      ">{t("skillsPickerHint")}</span>
    </div>

    <div style="overflow-y: auto; flex: 1; min-height: 0;">
      {#if results.length === 0}
        <div style="
          padding: 18px 12px;
          text-align: center;
          font-size: 11px;
          color: var(--chat-text-muted);
          font-style: italic;
        ">{t("skillsPickerEmpty")}</div>
      {:else}
        {#each results as skill, idx (skill.id)}
          {@const Icon = iconFor(skill.icon)}
          {@const isHi = idx === highlighted}
          <button
            type="button"
            role="option"
            aria-selected={isHi}
            onclick={() => onSelect(skill)}
            onmouseenter={() => (highlighted = idx)}
            style="
              width: 100%;
              display: flex;
              align-items: flex-start;
              gap: 10px;
              padding: 8px 10px;
              border-radius: var(--chat-radius);
              background: {isHi ? 'var(--chat-accent-subtle)' : 'transparent'};
              border: none;
              text-align: left;
              cursor: pointer;
              transition: background 0.1s;
            "
          >
            <div style="
              width: 28px; height: 28px;
              border-radius: 8px;
              background: var(--chat-bg-secondary);
              display: flex; align-items: center; justify-content: center;
              color: var(--chat-accent);
              flex-shrink: 0;
            ">
              <Icon size={15} />
            </div>
            <div style="flex: 1; min-width: 0;">
              <div style="
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 1px;
              ">
                <span style="
                  font-size: 12px;
                  font-weight: 600;
                  color: var(--chat-text-primary);
                ">{skill.name[i18n.locale]}</span>
                <span style="
                  font-family: var(--chat-font-mono);
                  font-size: 9px;
                  padding: 1px 5px;
                  border-radius: 99px;
                  background: var(--chat-bg-secondary);
                  color: var(--chat-text-muted);
                ">/{skill.id}</span>
              </div>
              <div style="
                font-size: 10.5px;
                color: var(--chat-text-muted);
                line-height: 1.4;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              ">{skill.description[i18n.locale]}</div>
            </div>
          </button>
        {/each}
      {/if}
    </div>
  </div>
{/if}
